/**
 * One person's flags, in detail.
 * Route: /oversight/:id
 * @file src/web/features/oversight/pages/[id].tsx
 *
 * @llm-rule WHEN: A Head or CEO drills into someone from the oversight list
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Skeleton,
  Textarea,
  toast,
} from '@bloomneo/uikit';
import { ArrowLeft, Flag, ListChecks, ShieldOff } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { PersonMonthDialog } from '../../reports/components/PersonMonthDialog';
import { Panel, PersonAvatar, SectionLabel } from '../../../shared/components/presence';
import { StatTiles } from '../components/OversightChrome';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';
import { FLAG_LABEL, FLAG_RULE, type FlagCode } from '../../../shared/flags';

interface Person {
  id: string;
  name: string | null;
  email: string;
  orgRole: OrgRole;
  title: string | null;
  initials: string | null;
}

interface FlagRow {
  id: string;
  code: FlagCode | null;
  body: string;
  day: string;
  status: string;
  createdAt: string;
  waivedAt: string | null;
  waiveReason: string | null;
}

interface TaskRow {
  id: string;
  title: string;
  dueAt: string;
  status: string;
  completedAt: string | null;
  closedLate: boolean;
  delayReason: string | null;
  assignedBy: { id: string; name: string | null; orgRole: OrgRole } | null;
  /** Who signed it off — often not the person who set it. */
  reviewedBy?: { id: string; name: string | null; orgRole: OrgRole } | null;
  submittedAt?: string | null;
  revisionCount?: number;
}

function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function PersonFlagsPage() {
  const { id } = useParams();
  const { request } = useAppRequest();

  const [person, setPerson] = useState<Person | null>(null);
  const [flags, setFlags] = useState<FlagRow[]>([]);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const { user: viewer } = useAuth();
  const [loading, setLoading] = useState(true);
  const [waiving, setWaiving] = useState<FlagRow | null>(null);
  const [waiveReason, setWaiveReason] = useState('');
  /** The monthly-report popup, shown over this page so closing it lands you
   *  back on Company today rather than on the report list. */
  const [monthOpen, setMonthOpen] = useState(false);
  /** The report popup always opens on the current month. */
  const nowUtc = new Date();

  const load = useCallback(async () => {
    const result = await request<{ person: Person; flags: FlagRow[]; tasks: TaskRow[] }>(
      `/api/oversight/people/${id}/flags`,
    );
    if (result.ok) {
      setPerson(result.data.person);
      setFlags(result.data.flags);
      setTasks(result.data.tasks);
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  }, [request, id]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Waive a flag — "the reason was fair, give them the chance".
   * The row is kept: it drops out of the open count but stays on record,
   * which is what keeps the count worth trusting.
   */
  async function waive() {
    if (!waiving || !waiveReason.trim()) return;

    const result = await request(`/api/oversight/flags/${waiving.id}/waive`, {
      method: 'POST',
      body: { reason: waiveReason.trim() },
    });
    if (!result.ok) return toast.error(result.error);

    toast.success('Flag waived');
    setWaiving(null);
    setWaiveReason('');
    void load();
  }

  // Group by rule so a pattern is obvious at a glance.
  const byCode = flags.reduce<Record<string, FlagRow[]>>((acc, f) => {
    const key = f.code ?? 'OTHER';
    (acc[key] ??= []).push(f);
    return acc;
  }, {});

  return (
    <AppShell wide>
      <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
        <Link to="/oversight">
          <ArrowLeft className="mr-1.5 size-4" />
          Back
        </Link>
      </Button>

      {loading ? (
        <Skeleton className="h-96 w-full rounded-[18px]" />
      ) : !person ? (
        <Panel>
          <p className="text-[13.5px] text-ink-soft">That person could not be loaded.</p>
        </Panel>
      ) : (
        <div className="mx-auto flex max-w-3xl flex-col gap-5">
          <Panel>
            <div className="flex items-center gap-3.5">
              <PersonAvatar person={person} size={44} />
              <div className="min-w-0">
                <h1 className="text-[19px] font-semibold tracking-tight text-ink">{person.name}</h1>
                <p className="text-[12.5px] text-ink-faint">
                  {person.title || ROLE_LABEL[person.orgRole]} · {person.email}
                </p>
              </div>
            </div>
          </Panel>

          {/* The two numbers that matter about this person, right now. */}
          <StatTiles
            tiles={[
              {
                n: flags.filter((f) => f.status !== 'WAIVED' && !f.waivedAt).length,
                label: 'open flags',
                tone: flags.some((f) => !f.waivedAt) ? 'alert' : 'plain',
              },
              {
                n: tasks.filter((x) => x.status !== 'DONE' && new Date(x.dueAt) < new Date()).length,
                label: 'overdue tasks',
                tone: 'quiet',
              },
            ]}
          />

          {/* Where else to look for this person, without going hunting. */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="h-11 flex-1 rounded-[13px]" asChild>
              <Link to={`/calendar?subjectId=${person.id}`}>View calendar</Link>
            </Button>
            {/* Opens here rather than navigating to /reports. Going there and
                back left you stranded on the report list after closing the
                popup, which is not where you started. */}
            <Button
              variant="outline"
              className="h-11 flex-1 rounded-[13px]"
              onClick={() => setMonthOpen(true)}
            >
              View monthly report
            </Button>
          </div>

          {/* Flags grouped by the rule that raised them */}
          <Panel padded={false} className="overflow-hidden">
            <div className="flex items-center gap-2 border-b border-rule bg-paper-warm px-5 py-2.5">
              <Flag className="size-3.5 text-ink-faint" />
              <SectionLabel>Flags</SectionLabel>
            </div>

            {flags.length === 0 ? (
              <p className="px-5 py-10 text-center text-[13px] text-ink-faint">
                No flags. Everything on time, on site, and on schedule.
              </p>
            ) : (
              <ul className="divide-y divide-rule">
                {Object.entries(byCode).map(([code, rows]) => (
                  <li key={code} className="px-5 py-4">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-[13.5px] font-semibold text-ink">
                        {code === 'OTHER' ? 'Other' : FLAG_LABEL[code as FlagCode]}
                      </span>
                      <span className="rounded-full bg-[var(--state-bad-bg)] px-2 py-0.5 text-[11px] font-semibold text-flag">
                        {rows.length}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11.5px] text-ink-ghost">
                      {code === 'OTHER' ? '' : FLAG_RULE[code as FlagCode]}
                    </p>

                    <ul className="mt-2 flex flex-col gap-1.5">
                      {rows.map((row) => (
                        <li
                          key={row.id}
                          className="rounded-lg bg-paper-warm px-3 py-2 text-[12.5px]"
                          style={row.waivedAt ? { opacity: 0.6 } : undefined}
                        >
                          <div className="flex flex-wrap items-baseline gap-2">
                            <span className="font-medium text-ink">{dayLabel(row.day)}</span>
                            <span className="min-w-0 flex-1 text-ink-soft">{row.body}</span>

                            {row.waivedAt ? (
                              <span className="rounded-full bg-[var(--state-ok-bg)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-appreciation">
                                waived
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 rounded-full px-2.5 text-[10.5px]"
                                onClick={() => {
                                  setWaiving(row);
                                  setWaiveReason('');
                                }}
                              >
                                <ShieldOff className="mr-1 size-3" />
                                Waive
                              </Button>
                            )}
                          </div>

                          {row.waiveReason && (
                            <p className="mt-1 text-[11.5px] italic text-ink-faint">
                              Waived — “{row.waiveReason}”
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {/* Their tasks */}
          <Panel padded={false} className="overflow-hidden">
            <div className="flex items-center gap-2 border-b border-rule bg-paper-warm px-5 py-2.5">
              <ListChecks className="size-3.5 text-ink-faint" />
              <SectionLabel>Tasks</SectionLabel>
            </div>

            {tasks.length === 0 ? (
              <p className="px-5 py-8 text-center text-[13px] text-ink-faint">
                No tasks assigned.
              </p>
            ) : (
              <ul className="divide-y divide-rule">
                {tasks.map((task) => (
                  <li key={task.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-ink">{task.title}</div>
                      <div className="text-[11.5px] text-ink-faint">
                        due {new Date(task.dueAt).toLocaleString(undefined, {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                        {task.assignedBy &&
                          (task.assignedBy.id === viewer?.id ? (
                            // Named outright, because "from Meera Iyer" when you
                            // ARE Meera Iyer makes you look up your own name to
                            // work out whether this one is yours to chase.
                            <>
                              {' · '}
                              <b className="font-semibold text-ink">assigned by you</b>
                            </>
                          ) : (
                            ` · assigned by ${task.assignedBy.name} (${
                              ROLE_LABEL[task.assignedBy.orgRole]
                            })`
                          ))}
                        {/* Who accepted it. A Head can set work that the
                            assignee's own Manager signs off, and the closure is
                            meaningless without the name against it. */}
                        {task.status === 'DONE' && task.reviewedBy && (
                          <>
                            {' · '}
                            {task.reviewedBy.id === viewer?.id ? (
                              <b className="font-semibold text-ink">accepted by you</b>
                            ) : (
                              `accepted by ${task.reviewedBy.name} (${
                                ROLE_LABEL[task.reviewedBy.orgRole]
                              })`
                            )}
                          </>
                        )}
                        {task.status === 'SUBMITTED' && (
                          <>
                            {' · '}
                            <b className="font-semibold" style={{ color: 'var(--dot-offsite)' }}>
                              handed in, awaiting review
                            </b>
                          </>
                        )}
                        {task.completedAt && (
                          <>
                            {' · '}
                            <span className={task.closedLate ? 'text-offsite' : 'text-appreciation'}>
                              {task.closedLate ? 'closed late' : 'closed on time'} on{' '}
                              {new Date(task.completedAt).toLocaleString(undefined, {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false,
                              })}
                            </span>
                          </>
                        )}
                      </div>

                      {task.delayReason && (
                        <p className="mt-1 rounded-lg bg-[var(--state-warn-bg)] px-2.5 py-1.5 text-[11.5px] leading-relaxed text-ink">
                          <b className="font-semibold">Their reason</b> — “{task.delayReason}”
                        </p>
                      )}
                    </div>
                    <span
                      className="rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.06em]"
                      style={{
                        background:
                          task.status === 'DONE'
                            ? 'var(--state-ok-bg)'
                            : task.status === 'OVERDUE'
                              ? 'var(--state-bad-bg)'
                              : 'var(--state-warn-bg)',
                        color:
                          task.status === 'DONE'
                            ? 'var(--dot-appreciation)'
                            : task.status === 'OVERDUE'
                              ? 'var(--dot-flag)'
                              : 'var(--dot-offsite)',
                      }}
                    >
                      {task.status.toLowerCase()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      )}
      <Dialog open={waiving !== null} onOpenChange={(open) => !open && setWaiving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Waive this flag?</DialogTitle>
            <DialogDescription>
              {waiving && (
                <>
                  {dayLabel(waiving.day)} — {waiving.body} The flag stays on record but stops
                  counting against them.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <FormField label="Why are you waiving it?" required>
            <Textarea
              value={waiveReason}
              onChange={(e) => setWaiveReason(e.target.value)}
              rows={3}
              placeholder="Was blocked on staging — fair reason, giving them the chance."
              className="resize-none"
            />
          </FormField>

          <DialogFooter>
            <Button variant="outline" onClick={() => setWaiving(null)}>
              Cancel
            </Button>
            <Button disabled={!waiveReason.trim()} onClick={() => void waive()}>
              Waive flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {person && (
        <PersonMonthDialog
          personId={monthOpen ? person.id : null}
          personName={person.name ?? ''}
          tab="flags"
          year={nowUtc.getUTCFullYear()}
          month={nowUtc.getUTCMonth() + 1}
          monthLabel={nowUtc.toLocaleDateString(undefined, {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
          })}
          onClose={() => setMonthOpen(false)}
        />
      )}

    </AppShell>
  );
}

export default function PersonFlagsRoute() {
  return (
    <AuthGuard>
      <PersonFlagsPage />
    </AuthGuard>
  );
}
